// src/pages/RideCheckout.tsx — HEARTLAS RIDES v2
// ─────────────────────────────────────────────────────────────────────────────
// CHANGELOG (sync with BookRide.tsx v8)
// ✅ Removed the nonexistent `selectServiceFee` import — same dead selector
//    problem as BookRide had. Fees now read directly off `state.riderServiceFees`.
// ✅ `fees.guestFee` → `fees.guestsFee` (the actual field the slice returns).
// ✅ `calculateRideEarnings` was being called with `extras`/`selectedExtras`,
//    which the thunk silently ignores — it only accepts a numeric `extraCharges`.
//    Extras were therefore NEVER reflected in the price. Fixed via sumSelectedExtras.
// ✅ Rate type is no longer rider-selectable via chips — it's owned by the
//    driver's listing, exactly like BookRide. `resolveRateType` defaults to
//    "perSurge" whenever a listing doesn't set one. The options step now
//    renders the correct inputs for whichever rate type the listing uses
//    (adults/children/infants/pets for perPerson, seat picker for perSeat,
//    day counter for perDay, info box for perCar/perKm/perSurge).
// ✅ Removed the redux thunks that don't exist on driversSlice
//    (`listenToActiveTrip`, `listenToRiderTripStatus`, `clearActiveTrip`,
//    `selectActiveTrip`) — replaced with direct RTDB listeners on
//    `riderTrips/{userId}/{tripId}`, same pattern as BookRide.
// ✅ Replaced `loadStripe` + `stripe.redirectToCheckout({ sessionId })` with
//    the same CheckoutResponse + redirectFromResponse pattern BookRide uses
//    (tolerant of several response shapes, no Stripe.js dependency needed).
// ✅ Cloud Function domain fixed: europe-west3-heartlas (was xsafari).
// ✅ Cosmoola currency (`useCurrency`) used for every price shown, not raw "€".
// ✅ Idempotency key + hard submit-lock ref (state alone isn't race-proof).
// ✅ Return-from-Stripe handling: reads `tripId` from the URL on mount to
//    reattach the live trip listener, plus a fallback pointer listener at
//    `activeRiderTrip/{userId}` for resuming a trip on a fresh page load.
// ✅ Cancelled-checkout recovery via sessionStorage (re-offers the same driver).
// ✅ Realtime DB connectivity banner (so a dropped socket doesn't fail silently).
// ✅ Full Heartlas gold/red re-theme, matching BookRide/AtlasHome design tokens.
// ─────────────────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { ref as dbRef, onValue, off, get as dbGet } from "firebase/database";
import { doc, getDoc } from "firebase/firestore";
import { db, rtdb } from "../../../firebase"; // ← adjust path
import type { AppDispatch, RootState } from "../../../redux/store";
import { calculateRideEarnings } from "../../../redux/riderServiceFeeSlice";
import type { RideRateType } from "../../../redux/riderServiceFeeSlice";
import { useCurrency } from "@cosmoola/react-cosmoola-js";
import {
  ChevronLeft, Car, Navigation, Clock, Users, Star,
  CheckCircle, X, ArrowRight, CreditCard, Loader2,
  AlertCircle, RotateCcw, Home, Flag, Minus, Plus,
  Sparkles, Baby, Dog, Info, PhoneCall, CloudOff,
} from "lucide-react";

/* ─── Cloud Function endpoints ───────────────────────────────────────────── */
const FN_BASE           = "https://europe-west3-heartlas.cloudfunctions.net";
const RIDE_CHECKOUT_URL = `${FN_BASE}/createRideCheckoutSession`;
const CANCEL_RIDE_URL   = `${FN_BASE}/cancelRide`;

const PENDING_RIDE_KEY = "heartlas_pending_ride"; // sessionStorage — survives the Stripe round-trip

type Step = "dropoff" | "options" | "pricing" | "trip";
type RRT  = RideRateType; // "perPerson" | "perPersonPerKm" | "perKm" | "perDay" | "perCar" | "perSeat" | "perSurge"
const DEFAULT_RATE_TYPE: RRT = "perSurge";
/** Every place that used to fall back to "perKm" now falls back to perSurge. */
const resolveRateType = (rt?: string | null): RRT => ((rt as RRT) || DEFAULT_RATE_TYPE);

const RATE_LABEL: Record<string, string> = {
  perKm: "Per Kilometre", perPerson: "Per Person", perDay: "Per Day",
  perCar: "Flat Rate / Trip", perSeat: "Per Seat", perPersonPerKm: "Per Person / Km",
  perSurge: "Dynamic Fare",
};

/** Sums only the extras the rider actually selected, using the driver's own price list.
 *  This is what was missing before — the thunk needs a number, not a map of keys. */
const sumSelectedExtras = (allExtras: Record<string, number> | undefined, selected: string[]): number => {
  if (!allExtras || !selected?.length) return 0;
  return selected.reduce((sum, key) => sum + (Number(allExtras[key]) || 0), 0);
};
/** RFC4122-ish fallback for browsers/webviews without crypto.randomUUID. */
const makeIdempotencyKey = (): string => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return `rk_${Date.now()}_${Math.random().toString(36).slice(2)}`;
};

/* ─── Haversine ──────────────────────────────────────────────────────────── */
function hav(a: number, b: number, c: number, d: number): number {
  const dLat = ((c - a) * Math.PI) / 180;
  const dLng = ((d - b) * Math.PI) / 180;
  const x = Math.sin(dLat / 2) ** 2 + Math.cos((a * Math.PI) / 180) * Math.cos((c * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return +(6371 * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x))).toFixed(2);
}

/* ─── SVG map markers — ink pickup pin, brand-red dropoff pin, gold driver ── */
const mkPickup = () => "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(
  `<svg xmlns='http://www.w3.org/2000/svg' width='28' height='28'><circle cx='14' cy='14' r='10' fill='%231B0E06' stroke='white' stroke-width='3'/><circle cx='14' cy='14' r='4' fill='%23F2A93E'/></svg>`
);
const mkDropoff = () => "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(
  `<svg xmlns='http://www.w3.org/2000/svg' width='32' height='40'><path d='M16 0C7.2 0 0 7.2 0 16c0 12 16 24 16 24s16-12 16-24C32 7.2 24.8 0 16 0z' fill='%23C8202E'/><circle cx='16' cy='16' r='6' fill='white'/></svg>`
);
const mkDriver = () => "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(
  `<svg xmlns='http://www.w3.org/2000/svg' width='44' height='44'><circle cx='22' cy='22' r='20' fill='%23D4900F' stroke='white' stroke-width='3'/><path d='M10 25 L22 14 L34 25' fill='none' stroke='white' stroke-width='2.5' stroke-linejoin='round'/><rect x='13' y='24' width='18' height='9' rx='2' fill='white'/><circle cx='16' cy='34' r='2.5' fill='%23D4900F'/><circle cx='28' cy='34' r='2.5' fill='%23D4900F'/></svg>`
);

/* ─── Map style — warm, brand-aligned (matches BookRide) ─────────────────── */
const MAP_STYLE = [
  { elementType: "geometry", stylers: [{ color: "#ffffff" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#4A3B2C" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#ffffff" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#ffffff" }] },
  { featureType: "road", elementType: "geometry.stroke", stylers: [{ color: "#EEDFC4" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#FCF6EA" }] },
  { featureType: "road.highway", elementType: "geometry.stroke", stylers: [{ color: "#D4900F" }, { weight: 0.6 }] },
  { featureType: "road.highway", elementType: "labels.text.fill", stylers: [{ color: "#1B0E06" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#EAE2D3" }] },
  { featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] },
  { featureType: "poi", elementType: "geometry", stylers: [{ color: "#F5EEDD" }] },
  { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#EFE7D4" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
  { featureType: "landscape", elementType: "geometry", stylers: [{ color: "#FBF6EE" }] },
  { featureType: "administrative", elementType: "geometry.stroke", stylers: [{ color: "#DDCBA6" }] },
];

/* ─── Status config ──────────────────────────────────────────────────────── */
const STATUS_CFG: Record<string, { label: string; color: string; Icon: any; sub: string }> = {
  requested:      { label: "Finding Your Driver…", color: "#8A7660", Icon: Navigation,  sub: "Hang tight, we're matching you" },
  accepted:       { label: "Driver Accepted!",      color: "#1A9E6E", Icon: CheckCircle, sub: "Driver is on the way" },
  arriving:       { label: "Driver Arriving",       color: "#4A9EFF", Icon: Car,         sub: "Almost there…" },
  ongoing:        { label: "Trip in Progress",      color: "#1A9E6E", Icon: Navigation,  sub: "You're on your way" },
  completed:      { label: "Trip Complete 🎉",      color: "#1A9E6E", Icon: Flag,        sub: "Thanks for riding with Heartlas" },
  cancelled:      { label: "Ride Cancelled",        color: "#C8202E", Icon: X,           sub: "Your ride was cancelled" },
  no_driver:      { label: "No Driver Available",   color: "#C8202E", Icon: AlertCircle, sub: "No drivers found nearby" },
};

/* ─── CSS — Heartlas gold/red brand theme (matches BookRide tokens) ──────── */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;0,900;1,400;1,600&family=Syne:wght@400;500;600;700;800&display=swap');
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
:root{
  --w:#FFFFFF;--iv:#FBF6EE;--cr:#FCF6EA;--st:#EEDFC4;--ms:#C9B79B;
  --wm:#8A7660;--dk:#1B0E06;--ng:#1B0E06;
  --rd:#C8202E;--rdd:#9E1521;--rds:#FBE7E9;
  --gd:#F2A93E;--gdd:#D4900F;--gdp:#FCEFD4;--gdb:rgba(242,169,62,.38);
  --gn:#1A9E6E;--bl:#4A9EFF;
  --grad:linear-gradient(135deg,#1B0E06 0%,#D4900F 35%,#C8202E 65%,#F2A93E 100%);
  --gradh:linear-gradient(90deg,#1B0E06 0%,#D4900F 25%,#C8202E 60%,#F2A93E 100%);
  --serif:'Playfair Display',Georgia,serif;
  --sans:'Syne',-apple-system,sans-serif;
  --s1:0 2px 8px rgba(27,14,6,.06);--s2:0 10px 34px rgba(27,14,6,.12);--s3:0 4px 16px rgba(200,32,46,.28);
}
html,body{height:100%;overflow:hidden;background:var(--iv);}
::-webkit-scrollbar{width:0;height:0;}

@keyframes gradSpin{0%{background-position:0% 50%}50%{background-position:100% 50%}100%{background-position:0% 50%}}
@keyframes rcoSpin{to{transform:rotate(360deg);}}
@keyframes rcoUp{from{transform:translateY(100%);}to{transform:translateY(0);}}

.rco-root{width:100vw;height:100dvh;background:var(--w);font-family:var(--sans);color:var(--ng);overflow:hidden;display:flex;flex-direction:column;}
.rco-on .rco-rev{opacity:1!important;transform:none!important;}
.rco-rev{opacity:0;transform:translateY(12px);transition:opacity .4s ease var(--d,0ms),transform .4s cubic-bezier(.22,1,.36,1) var(--d,0ms);}

.rco-topbar{height:3px;background:var(--grad);background-size:200% 100%;animation:gradSpin 4s ease infinite;flex-shrink:0;}

/* BANNERS */
.rco-banner{display:flex;align-items:center;gap:9px;padding:9px 16px;font-size:11.5px;font-weight:700;flex-shrink:0;}
.rco-banner-offline{background:#3A2A1C;color:#F5DFC0;}
.rco-banner-resume{background:var(--gdp);color:var(--gdd);border-bottom:1px solid var(--gdb);}
.rco-banner-resume button{margin-left:auto;background:var(--ng);color:#fff;border:none;padding:6px 13px;border-radius:20px;font-size:10px;font-weight:800;letter-spacing:.5px;cursor:pointer;flex-shrink:0;}
.rco-banner-close{background:none;border:none;color:inherit;opacity:.6;cursor:pointer;display:flex;flex-shrink:0;}

/* HEADER */
.rco-hdr{flex-shrink:0;height:58px;background:var(--w);border-bottom:1px solid var(--st);display:flex;align-items:stretch;z-index:40;box-shadow:var(--s1);}
.rco-logo{display:flex;align-items:center;padding:0 16px;border-right:1px solid var(--st);cursor:pointer;flex-shrink:0;gap:9px;}
.rco-logo-ring{position:relative;width:24px;height:24px;border-radius:8px;display:flex;align-items:center;justify-content:center;flex-shrink:0;}
.rco-logo-ring::before{content:"";position:absolute;inset:-2px;border-radius:10px;background:var(--grad);background-size:300% 100%;animation:gradSpin 3s linear infinite;}
.rco-logo-ring-inner{position:relative;width:100%;height:100%;border-radius:8px;background:var(--w);display:flex;align-items:center;justify-content:center;}
.rco-logo-txt{font-family:var(--serif);font-size:15px;font-weight:800;letter-spacing:-.3px;
  background:var(--gradh);background-size:200% 100%;animation:gradSpin 5s ease infinite;
  -webkit-background-clip:text;background-clip:text;-webkit-text-fill-color:transparent;}
.rco-hdr-center{flex:1;display:flex;align-items:center;padding:0 14px;gap:10px;min-width:0;}
.rco-back{width:34px;height:34px;border-radius:50%;background:var(--cr);border:1px solid var(--st);display:flex;align-items:center;justify-content:center;cursor:pointer;color:var(--ng);flex-shrink:0;transition:all .2s;}
.rco-back:hover{background:var(--ng);color:var(--w);}
.rco-hdr-car{flex:1;display:flex;flex-direction:column;gap:2px;min-width:0;}
.rco-hdr-model{font-size:13px;font-weight:800;color:var(--ng);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.rco-hdr-sub{font-size:10px;color:var(--gdd);font-weight:700;}
.rco-hdr-r{display:flex;align-items:center;gap:8px;padding-right:14px;flex-shrink:0;}
.rco-eta-badge{background:var(--gdp);border:1px solid var(--gdb);padding:5px 12px;border-radius:14px;display:flex;flex-direction:column;align-items:center;}
.rco-eta-num{font-family:var(--serif);font-size:17px;font-weight:700;color:var(--gdd);line-height:1;}
.rco-eta-unit{font-size:8px;font-weight:700;color:var(--wm);text-transform:uppercase;letter-spacing:1px;}

/* BODY */
.rco-body{flex:1;display:flex;overflow:hidden;}
@media(min-width:780px){
  .rco-map-col{flex:1;position:relative;}
  .rco-panel-col{width:420px;flex-shrink:0;background:var(--w);border-left:1px solid var(--st);display:flex;flex-direction:column;overflow-y:auto;}
  .rco-mob-panel{display:none!important;}
}
@media(max-width:779px){
  .rco-body{flex-direction:column;position:relative;}
  .rco-map-col{flex:1;min-height:0;}
  .rco-panel-col{display:none!important;}
  .rco-mob-panel{position:absolute;bottom:0;left:0;right:0;max-height:68dvh;background:var(--w);border-top:1.5px solid var(--st);border-top-left-radius:20px;border-top-right-radius:20px;box-shadow:0 -10px 44px rgba(27,14,6,.14);overflow-y:auto;z-index:20;animation:rcoUp .32s cubic-bezier(.22,1,.36,1) both;}
}

/* MAP */
.rco-map-wrap{width:100%;height:100%;position:relative;}
.rco-map-spinner{position:absolute;inset:0;background:var(--cr);display:flex;align-items:center;justify-content:center;z-index:5;}
.rco-spinner{width:30px;height:30px;border-radius:50%;border:2px solid var(--st);border-top-color:var(--rd);animation:rcoSpin .85s linear infinite;}
.rco-spin-sm{width:16px;height:16px;border-radius:50%;border:2px solid rgba(255,255,255,.35);border-top-color:#fff;animation:rcoSpin .7s linear infinite;flex-shrink:0;}

/* PANEL */
.rco-handle{width:34px;height:3.5px;background:var(--ms);border-radius:2px;margin:11px auto 4px;display:none;}
@media(max-width:779px){.rco-handle{display:block;}}
.rco-panel-inner{padding:20px 20px 44px;}
.rco-step-title{font-family:var(--serif);font-size:24px;font-weight:700;color:var(--ng);margin-bottom:3px;letter-spacing:-.3px;}
.rco-step-sub{font-size:11px;color:var(--wm);font-weight:500;margin-bottom:16px;}

/* driver strip */
.rco-dstrip{position:relative;background:var(--cr);border:1.5px solid var(--st);border-radius:14px;padding:14px 14px 14px 18px;margin-bottom:16px;display:flex;align-items:center;gap:12px;overflow:hidden;box-shadow:var(--s1);}
.rco-dstrip::before{content:'';position:absolute;left:0;top:0;bottom:0;width:3px;background:var(--grad);}
.rco-avatar{width:46px;height:46px;border-radius:50%;border:2px solid var(--st);object-fit:cover;flex-shrink:0;}
.rco-avatar-fb{width:46px;height:46px;border-radius:50%;background:var(--gdp);border:2px solid var(--st);display:flex;align-items:center;justify-content:center;color:var(--gdd);flex-shrink:0;}
.rco-d-info{flex:1;min-width:0;}
.rco-d-name{font-size:14px;font-weight:800;color:var(--ng);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.rco-d-vehicle{font-size:11px;color:var(--wm);margin-top:2px;font-weight:500;}
.rco-d-rating{display:flex;align-items:center;gap:3px;margin-top:4px;color:var(--gdd);font-size:11px;font-weight:700;}
.rco-d-phone{width:36px;height:36px;border-radius:50%;background:var(--w);border:1px solid var(--st);display:flex;align-items:center;justify-content:center;color:var(--ng);flex-shrink:0;}

/* address block */
.rco-addr-block{margin-bottom:14px;border-radius:14px;overflow:hidden;border:1.5px solid var(--st);box-shadow:var(--s1);}
.rco-addr-row{display:flex;align-items:flex-start;gap:10px;padding:12px 14px;background:var(--w);}
.rco-addr-row+.rco-addr-row{border-top:1px solid var(--cr);}
.rco-addr-dot{width:10px;height:10px;border-radius:50%;flex-shrink:0;margin-top:3px;}
.rco-dot-g{background:var(--gn);}
.rco-dot-r{background:var(--rd);}
.rco-addr-field{flex:1;min-width:0;}
.rco-addr-lbl{font-size:7.5px;font-weight:700;letter-spacing:2.5px;color:var(--ms);text-transform:uppercase;margin-bottom:4px;}
.rco-addr-val{font-size:13px;font-weight:600;color:var(--ng);line-height:1.4;}
.rco-addr-input{width:100%;background:transparent;border:none;outline:none;font-size:13px;font-weight:600;color:var(--ng);font-family:var(--sans);padding:2px 0;}
.rco-addr-input::placeholder{color:var(--ms);}
.rco-dist-row{display:flex;align-items:center;gap:7px;background:var(--gdp);border:1px solid var(--gdb);border-radius:10px;padding:9px 12px;margin-bottom:14px;color:var(--gdd);font-size:12px;font-weight:700;}

/* section label */
.rco-sect-lbl{font-size:8px;font-weight:700;letter-spacing:2.5px;color:var(--ms);text-transform:uppercase;margin-bottom:9px;}

/* rate-type badge (read-only, owned by the listing) */
.rco-rate-badge{display:inline-flex;align-items:center;gap:6px;background:var(--gdp);border:1px solid var(--gdb);padding:6px 13px;border-radius:20px;font-size:11px;font-weight:800;color:var(--gdd);margin-bottom:16px;}

/* counters */
.rco-ctr-row{display:flex;align-items:center;justify-content:space-between;padding:11px 0;border-bottom:1px solid var(--cr);}
.rco-ctr-lbl{display:flex;align-items:center;gap:8px;font-size:12px;font-weight:600;color:var(--wm);}
.rco-ctr-lbl-txt{}
.rco-ctr-sub{font-size:10px;color:var(--ms);margin-top:1px;}
.rco-ctr-ctrl{display:flex;align-items:center;gap:5px;}
.rco-cbtn{width:32px;height:32px;border-radius:50%;border:1.5px solid var(--st);background:var(--w);display:flex;align-items:center;justify-content:center;cursor:pointer;color:var(--ng);transition:all .2s;}
.rco-cbtn:hover:not(:disabled){background:var(--ng);color:var(--w);border-color:var(--ng);}
.rco-cbtn:disabled{opacity:.28;cursor:default;}
.rco-cval{color:var(--ng);font-family:var(--serif);font-size:18px;font-weight:700;width:28px;text-align:center;}

/* seat grid */
.rco-seat-grid{display:flex;flex-wrap:wrap;gap:7px;margin:11px 0 4px;}
.rco-seat-chip{display:flex;align-items:center;gap:4px;padding:8px 14px;border:1.5px solid var(--st);border-radius:30px;cursor:pointer;background:var(--w);font-size:12px;font-weight:700;color:var(--ng);font-family:var(--sans);transition:all .2s;}
.rco-seat-chip.on{background:var(--ng);border-color:var(--ng);color:var(--w);}

/* info box */
.rco-info-box{display:flex;gap:9px;background:var(--gdp);border:1px solid var(--gdb);padding:12px 13px;margin:4px 0 16px;border-radius:12px;}
.rco-info-txt{font-size:11px;color:var(--dk);line-height:1.65;}

/* extras */
.rco-ext-sec{margin:6px 0 16px;}
.rco-ext-lbl{font-size:8px;font-weight:700;letter-spacing:2.5px;color:var(--ms);text-transform:uppercase;margin-bottom:9px;}
.rco-ext-row{display:flex;align-items:center;padding:10px 0;border-bottom:1px solid var(--cr);gap:10px;cursor:pointer;}
.rco-ext-chk{width:22px;height:22px;border-radius:50%;border:1.5px solid var(--st);flex-shrink:0;display:flex;align-items:center;justify-content:center;transition:all .2s;}
.rco-ext-chk.on{background:var(--gn);border-color:var(--gn);}
.rco-ext-name{flex:1;font-size:12px;font-weight:600;color:var(--ng);}
.rco-ext-price{font-size:12px;font-weight:700;color:var(--ng);}

/* sharing */
.rco-share-row{display:flex;align-items:center;justify-content:space-between;background:var(--cr);border:1px solid var(--st);border-radius:14px;padding:14px;margin-bottom:14px;cursor:pointer;}
.rco-share-title{font-size:13px;font-weight:700;color:var(--ng);}
.rco-share-sub{font-size:11px;color:var(--wm);margin-top:2px;}
.rco-tog{width:40px;height:22px;border-radius:11px;background:var(--st);position:relative;transition:background .2s;flex-shrink:0;}
.rco-tog.on{background:var(--gn);}
.rco-tog-thumb{position:absolute;top:3px;left:3px;width:16px;height:16px;border-radius:50%;background:var(--w);transition:left .2s;box-shadow:0 1px 4px rgba(0,0,0,.15);}
.rco-tog.on .rco-tog-thumb{left:21px;}

/* notes */
.rco-notes{width:100%;background:var(--cr);border:1px solid var(--st);border-radius:12px;padding:12px;color:var(--ng);font-size:13px;font-family:var(--sans);resize:none;outline:none;margin-bottom:16px;}
.rco-notes::placeholder{color:var(--ms);}

/* route compact */
.rco-route-strip{display:flex;align-items:center;gap:6px;background:var(--cr);border:1px solid var(--st);border-radius:10px;padding:10px 12px;margin-bottom:14px;overflow:hidden;}
.rco-route-dot{width:8px;height:8px;border-radius:50%;flex-shrink:0;}
.rco-route-addr{color:var(--dk);font-size:11px;font-weight:600;flex:1;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.rco-route-sep{color:var(--ms);font-size:9px;flex-shrink:0;}

/* price card */
.rco-price-card{position:relative;background:var(--cr);border:1px solid var(--st);border-radius:14px;padding:16px;margin-bottom:14px;overflow:hidden;}
.rco-price-card::before{content:'';position:absolute;top:0;left:0;right:0;height:2px;background:var(--grad);background-size:200% 100%;animation:gradSpin 5s ease infinite;}
.rco-price-row{display:flex;justify-content:space-between;align-items:center;padding:5px 0;}
.rco-price-lbl{color:var(--dk);font-size:12px;}
.rco-price-val{color:var(--ng);font-size:12px;font-weight:700;}
.rco-price-div{height:1px;background:var(--st);margin:7px 0;}
.rco-total-row{display:flex;justify-content:space-between;align-items:center;padding:8px 0 4px;}
.rco-total-lbl{color:var(--ng);font-size:14px;font-weight:700;}
.rco-total-val{font-family:var(--serif);font-size:23px;font-weight:800;
  background:var(--gradh);background-size:200% 100%;animation:gradSpin 3s ease infinite;
  -webkit-background-clip:text;background-clip:text;-webkit-text-fill-color:transparent;}
.rco-price-note{font-size:9.5px;color:var(--wm);margin-top:7px;}

/* buttons */
.rco-pay-btn{width:100%;padding:15px 0;background:var(--grad);background-size:200% 100%;animation:gradSpin 6s ease infinite;border:none;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:9px;font-size:10px;font-weight:700;letter-spacing:2.5px;text-transform:uppercase;color:var(--w);font-family:var(--sans);transition:all .2s;margin-bottom:10px;border-radius:12px;box-shadow:var(--s3);}
.rco-pay-btn:hover:not(:disabled){filter:brightness(1.08);}
.rco-pay-btn:disabled{opacity:.45;cursor:not-allowed;animation:none;}
.rco-back-lnk{background:none;border:none;color:var(--wm);font-size:12px;font-family:var(--sans);cursor:pointer;display:block;text-align:center;padding:4px;transition:color .2s;}
.rco-back-lnk:hover{color:var(--ng);}
.rco-stripe-note{color:var(--ms);font-size:10px;text-align:center;line-height:1.5;margin-top:8px;}
.rco-err{display:flex;align-items:center;gap:9px;background:var(--rds);border:1px solid rgba(200,32,46,.3);border-radius:10px;padding:10px 12px;margin-bottom:12px;color:var(--rd);font-size:12px;font-weight:600;}
.rco-err button{background:none;border:none;color:var(--rd);cursor:pointer;flex-shrink:0;margin-left:auto;}
.rco-cont-btn{width:100%;padding:14px 0;background:var(--grad);background-size:200% 100%;animation:gradSpin 6s ease infinite;border:none;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:8px;font-size:10px;font-weight:700;letter-spacing:2px;text-transform:uppercase;color:var(--w);font-family:var(--sans);transition:all .2s;border-radius:12px;box-shadow:var(--s3);}
.rco-cont-btn:hover{filter:brightness(1.08);}

/* trip */
.rco-status-badge{display:flex;align-items:center;gap:10px;border:1.5px solid;border-radius:14px;padding:14px;margin-bottom:14px;}
.rco-status-lbl{font-size:15px;font-weight:800;}
.rco-status-sub{font-size:10px;color:var(--wm);margin-top:2px;}
.rco-trip-driver{display:flex;align-items:center;gap:12px;background:var(--cr);border:1px solid var(--st);border-radius:14px;padding:14px;margin-bottom:12px;}
.rco-trip-icon{width:44px;height:44px;border-radius:50%;background:var(--gdp);border:1px solid var(--gdb);display:flex;align-items:center;justify-content:center;color:var(--gdd);flex-shrink:0;overflow:hidden;}
.rco-trip-icon img{width:100%;height:100%;object-fit:cover;}
.rco-trip-info{flex:1;min-width:0;}
.rco-trip-info-lbl{font-size:9px;font-weight:700;letter-spacing:1.5px;color:var(--ms);text-transform:uppercase;margin-bottom:3px;}
.rco-trip-info-val{font-size:13px;font-weight:700;color:var(--ng);}
.rco-trip-route{display:flex;flex-direction:column;gap:6px;margin-bottom:14px;}
.rco-trip-addr{display:flex;align-items:center;gap:8px;}
.rco-trip-dot{width:8px;height:8px;border-radius:50%;flex-shrink:0;}
.rco-trip-txt{font-size:12px;font-weight:600;color:var(--dk);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.rco-trip-price-row{display:flex;justify-content:space-between;align-items:center;background:var(--cr);border:1px solid var(--st);border-radius:14px;padding:12px 14px;margin-bottom:12px;}
.rco-trip-price-lbl{font-size:11px;color:var(--wm);}
.rco-trip-price-val{font-family:var(--serif);font-size:20px;font-weight:700;color:var(--ng);}
.rco-cancel-btn{width:100%;padding:13px 0;background:none;border:1px solid rgba(200,32,46,.35);border-radius:12px;cursor:pointer;color:var(--rd);font-size:12px;font-weight:700;font-family:var(--sans);margin-bottom:8px;transition:background .15s;display:flex;align-items:center;justify-content:center;gap:6px;}
.rco-cancel-btn:hover{background:var(--rds);}
.rco-done-btn{width:100%;padding:14px 0;background:var(--ng);border:none;border-radius:12px;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:8px;font-size:10px;font-weight:700;letter-spacing:2px;text-transform:uppercase;color:var(--w);font-family:var(--sans);transition:all .2s;}
.rco-done-btn:hover{background:var(--rd);}

.pac-container{z-index:99999!important;border:1px solid #EEDFC4!important;border-top:none!important;background:#fff!important;font-family:'Syne',-apple-system,sans-serif!important;box-shadow:0 8px 32px rgba(27,14,6,.12)!important;}
.pac-item{font-size:12px!important;color:#1B0E06!important;padding:9px 14px!important;cursor:pointer!important;}
.pac-item:hover{background:#FCF6EA!important;}
.pac-item-query{font-weight:700!important;}
`;

/* ─── CounterRow ─────────────────────────────────────────────────────────── */
function CounterRow({ label, sub, value, onInc, onDec, minVal = 0, icon }: any) {
  return (
    <div className="rco-ctr-row">
      <div className="rco-ctr-lbl">
        {icon && <span style={{ color: "var(--gdd)", display: "flex" }}>{icon}</span>}
        <div><div className="rco-ctr-lbl-txt">{label}</div>{sub && <div className="rco-ctr-sub">{sub}</div>}</div>
      </div>
      <div className="rco-ctr-ctrl">
        <button className="rco-cbtn" onClick={onDec} disabled={value <= minVal}><Minus size={13} /></button>
        <div className="rco-cval">{value}</div>
        <button className="rco-cbtn" onClick={onInc}><Plus size={13} /></button>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/* MAIN                                                                         */
/* ═══════════════════════════════════════════════════════════════════════════ */
export default function RideCheckout() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((s: RootState) => s.auth);

  // ── Fees — read the slice's own state directly, same as BookRide. There is
  //    no `selectServiceFee` selector exported anywhere in riderServiceFeeSlice.ts.
  //    Adjust the key below ("riderServiceFees") if your root reducer registers
  //    this slice under a different name. ──
  const fees = useSelector((s: RootState) => (s as any).riderServiceFees);

  // ── Cosmoola currency — prices are stored/computed in EUR server-side and
  //    converted for display only. ──
  const { convertRaw, symbol } = useCurrency();
  const money = useCallback((amountEur: number | undefined | null) => {
    const n = Number(amountEur ?? 0);
    return `${symbol}${convertRaw(n, "EUR").toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }, [convertRaw, symbol]);

  const st = (location.state as any) || {};
  const {
    listingId, listingData: il, driverId,
    riderId: pr, pickupLat: iLat, pickupLng: iLng,
    pickupAddress: iAddr, driverLat, driverLng,
    etaMinutes: iEta,
  } = st;

  const riderId = pr || (user as any)?.userId || (user as any)?.uid || "guest";

  /* ── Google Maps ── */
  const [isLoaded, setIsLoaded] = useState(typeof (window as any).google !== "undefined");
  useEffect(() => {
    if (isLoaded) return;
    const t = setInterval(() => {
      if (typeof (window as any).google !== "undefined") { setIsLoaded(true); clearInterval(t); }
    }, 100);
    return () => clearInterval(t);
  }, [isLoaded]);

  const mapRef     = useRef<any>(null);
  const acRef      = useRef<any>(null);
  const inputRef   = useRef<HTMLInputElement>(null);
  const markersRef = useRef<any[]>([]);
  const dirRef     = useRef<any>(null);

  /* ── State ── */
  const [listing,  setListing]  = useState<any>(il || null);
  const [livePos,  setLivePos]  = useState<{ lat: number; lng: number } | null>(
    driverLat && driverLng ? { lat: driverLat, lng: driverLng } : null
  );
  const [pickup,   setPickup]   = useState({ lat: iLat || 0, lng: iLng || 0, name: iAddr || "Your Location" });
  const [dropoff,  setDropoff]  = useState<{ lat: number; lng: number; name: string } | null>(null);
  const [dirs,     setDirs]     = useState<any>(null);
  const [distKm,   setDistKm]   = useState(0);
  const [eta,      setEta]      = useState(iEta || 0);

  // rate-type-driven inputs — owned by the listing, not chosen by the rider
  const [adults,         setAdults]         = useState(1);
  const [children,       setChildren]       = useState(0);
  const [infants,        setInfants]        = useState(0);
  const [pets,           setPets]           = useState(0);
  const [serviceAnimals, setServiceAnimals] = useState(0);
  const [seats,          setSeats]          = useState(1);
  const [days,           setDays]           = useState(1);
  const [sharing,        setSharing]        = useState(false);
  const [selectedExtras, setSelectedExtras] = useState<string[]>([]);
  const [notes,          setNotes]          = useState("");

  const [step,     setStep]     = useState<Step>("dropoff");
  const [booking,  setBooking]  = useState(false);
  const [bookingErr, setBookingErr] = useState("");
  const [mounted,  setMounted]  = useState(false);

  const [activeTrip,   setActiveTrip]   = useState<any>(null);
  const [tripId,       setTripId]       = useState<string | null>(null);
  const [rtdbOffline,  setRtdbOffline]  = useState(false);
  const [resumeNotice, setResumeNotice] = useState<{ driverName: string } | null>(null);

  const submittingRef     = useRef(false);   // hard lock — state alone can race on double-click
  const idempotencyKeyRef = useRef<string>("");

  const rt      = resolveRateType(listing?.rateType);
  const isPerP  = rt === "perPerson" || rt === "perPersonPerKm";
  const maxCap  = parseInt(String(listing?.capacity || listing?.seats || 8));
  const pax     = adults + children;
  const extras  = listing?.extras ? Object.entries(listing.extras as Record<string, number>) : [];
  const extrasTotal = sumSelectedExtras(listing?.extras, selectedExtras);
  const toggleEx = (k: string) => setSelectedExtras(p => p.includes(k) ? p.filter(e => e !== k) : [...p, k]);

  /* CSS inject */
  useEffect(() => {
    const id = "rco-css-v2";
    if (!document.getElementById(id)) {
      const tag = document.createElement("style"); tag.id = id; tag.textContent = CSS;
      document.head.appendChild(tag);
    }
    requestAnimationFrame(() => setMounted(true));
  }, []);

  useEffect(() => { if (!riderId || riderId === "guest") { /* allow guest browsing of this page */ } }, [riderId]);

  useEffect(() => {
    if (listing || !listingId) return;
    getDoc(doc(db, "riders", listingId)).then((s) => { if (s.exists()) setListing({ id: s.id, ...s.data() }); }).catch(console.warn);
  }, [listingId, listing]);

  /* ── Return-from-Stripe: pick up ?tripId= from the success_url and reattach
        the live trip listener. Also handles ?checkout=cancelled by restoring
        the driver the rider had selected (stashed in sessionStorage right
        before the redirect to Stripe). Both params are stripped from the URL
        afterwards so a refresh doesn't replay them. ── */
  useEffect(() => {
    const qs = new URLSearchParams(location.search);
    const tripIdParam = qs.get("tripId");
    const checkoutParam = qs.get("checkout");

    if (tripIdParam) { setTripId(tripIdParam); setStep("trip"); }

    if (checkoutParam === "cancelled") {
      try {
        const raw = sessionStorage.getItem(PENDING_RIDE_KEY);
        if (raw) {
          const pending = JSON.parse(raw);
          setResumeNotice({ driverName: pending?.driverName || "your driver" });
        }
      } catch { /* ignore malformed sessionStorage */ }
    }

    if (tripIdParam || checkoutParam) {
      qs.delete("tripId"); qs.delete("checkout"); qs.delete("session_id");
      const clean = qs.toString();
      navigate({ pathname: location.pathname, search: clean ? `?${clean}` : "" }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── Fallback resume: if we land here fresh with no tripId in the URL but
        the rider has a trip mid-flight, pick it up from a pointer node your
        webhook is expected to maintain at `activeRiderTrip/{userId}`. ── */
  useEffect(() => {
    if (tripId || !riderId || riderId === "guest") return;
    (async () => {
      try {
        const snap = await dbGet(dbRef(rtdb, `activeRiderTrip/${riderId}`));
        const pointerId = snap.exists() ? snap.val()?.tripId ?? snap.val() : null;
        if (pointerId) { setTripId(pointerId); setStep("trip"); }
      } catch { /* pointer node may simply not exist yet — that's fine */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [riderId]);

  /* ── RTDB connectivity — surfaces dropped sockets instead of failing silently ── */
  useEffect(() => {
    const r = dbRef(rtdb, ".info/connected");
    const cb = onValue(r, snap => setRtdbOffline(snap.val() !== true));
    return () => off(r, "value", cb as any);
  }, []);

  /* ── Trip status listener — direct RTDB, no redux thunk that doesn't exist ── */
  useEffect(() => {
    if (!tripId || !riderId || riderId === "guest") return;
    const r = dbRef(rtdb, `riderTrips/${riderId}/${tripId}`);
    const cb = onValue(r, snap => {
      const v = snap.val();
      if (!v) return;
      setActiveTrip((prev: any) => ({ ...prev, ...v, tripId }));
    });
    return () => off(r, "value", cb as any);
  }, [tripId, riderId]);

  useEffect(() => {
    if (!driverId) return;
    const r = dbRef(rtdb, `onlineDrivers/${driverId}`);
    const cb = onValue(r, (snap) => { const v = snap.val(); if (v?.lat && v?.lng) setLivePos({ lat: v.lat, lng: v.lng }); });
    return () => off(r, "value", cb as any);
  }, [driverId]);

  /* Live fee preview — extraCharges is now a real number (was silently
     dropped before), and perSurge cases pass through base/perKm/perMinute/surge. */
  useEffect(() => {
    if (!listing) return;
    const passengers = isPerP ? adults : 1;
    const durationMinutes = eta || Math.round((distKm / 40) * 60);
    dispatch(calculateRideEarnings({
      rate:            listing.rate || 0,
      rateType:        rt,
      distanceKm:      distKm,
      passengers,
      seats:           rt === "perSeat" ? seats : 1,
      extraCharges:    extrasTotal,
      discountApplied: listing.discountApplied || {},
      durationDays:    rt === "perDay" ? days : 0,
      baseFare:        listing.baseFare || 0,
      perKmRate:       listing.perKmRate || 0,
      perMinuteRate:   listing.perMinuteRate || 0,
      durationMinutes,
      surgeMultiplier: listing.surgeMultiplier || 1,
    }));
  }, [listing, rt, distKm, adults, isPerP, seats, days, extrasTotal, eta, dispatch]);

  useEffect(() => {
    if (!isLoaded || !pickup.lat || !dropoff) return;
    new (window as any).google.maps.DirectionsService().route(
      { origin: { lat: pickup.lat, lng: pickup.lng }, destination: { lat: dropoff.lat, lng: dropoff.lng }, travelMode: (window as any).google.maps.TravelMode.DRIVING },
      (result: any, status: any) => {
        if (status === "OK" && result) {
          setDirs(result);
          const leg = result.routes[0].legs[0];
          setDistKm(+((leg.distance?.value || 0) / 1000).toFixed(2));
          setEta(Math.ceil((leg.duration?.value || 0) / 60));
        }
      }
    );
  }, [isLoaded, dropoff]);

  /* Init map */
  useEffect(() => {
    if (!isLoaded) return;
    const el = document.getElementById("rco-gmap");
    if (!el || mapRef.current) return;
    mapRef.current = new (window as any).google.maps.Map(el, {
      center: { lat: pickup.lat || -1.2921, lng: pickup.lng || 36.8219 },
      zoom: 13, styles: MAP_STYLE, disableDefaultUI: true, zoomControl: true,
    });
  }, [isLoaded]); // eslint-disable-line

  /* Update markers + route */
  useEffect(() => {
    if (!mapRef.current || !isLoaded) return;
    const g = (window as any).google.maps;
    markersRef.current.forEach((m) => m.setMap(null)); markersRef.current = [];
    if (pickup.lat !== 0) markersRef.current.push(new g.Marker({ position: { lat: pickup.lat, lng: pickup.lng }, map: mapRef.current, icon: { url: mkPickup(), scaledSize: new g.Size(28, 28), anchor: new g.Point(14, 14) } }));
    if (dropoff) markersRef.current.push(new g.Marker({ position: { lat: dropoff.lat, lng: dropoff.lng }, map: mapRef.current, icon: { url: mkDropoff(), scaledSize: new g.Size(32, 40), anchor: new g.Point(16, 40) } }));
    if (livePos) markersRef.current.push(new g.Marker({ position: { lat: livePos.lat, lng: livePos.lng }, map: mapRef.current, icon: { url: mkDriver(), scaledSize: new g.Size(44, 44), anchor: new g.Point(22, 22) } }));
    if (pickup.lat !== 0) {
      const bounds = new g.LatLngBounds();
      bounds.extend({ lat: pickup.lat, lng: pickup.lng });
      if (livePos) bounds.extend({ lat: livePos.lat, lng: livePos.lng });
      if (dropoff) bounds.extend({ lat: dropoff.lat, lng: dropoff.lng });
      mapRef.current.fitBounds(bounds, { top: 60, bottom: 60, left: 40, right: 40 });
    }
    if (dirs) {
      if (!dirRef.current) dirRef.current = new g.DirectionsRenderer({ suppressMarkers: true, polylineOptions: { strokeColor: "#C8202E", strokeWeight: 4, strokeOpacity: 0.9 } });
      dirRef.current.setMap(mapRef.current); dirRef.current.setDirections(dirs);
    } else if (dirRef.current) dirRef.current.setMap(null);
  }, [isLoaded, pickup, dropoff, livePos, dirs]);

  /* Autocomplete */
  useEffect(() => {
    if (!isLoaded || !inputRef.current || step !== "dropoff") return;
    acRef.current = new (window as any).google.maps.places.Autocomplete(inputRef.current, { fields: ["geometry", "formatted_address", "name"] });
    acRef.current.addListener("place_changed", () => {
      const place = acRef.current!.getPlace();
      const loc = place.geometry?.location;
      if (!loc) return;
      setDropoff({ lat: loc.lat(), lng: loc.lng(), name: place.formatted_address || place.name || "" });
      setStep("options");
    });
  }, [isLoaded, step]);

  /* ── Pay — same CheckoutResponse + redirectFromResponse pattern as BookRide,
        with idempotency key, submit lock, and cancelled-checkout recovery. ── */
  type CheckoutResponse = { id?: string; url?: string; data?: { url?: string }; sessionUrl?: string; checkoutUrl?: string };
  const redirectFromResponse = (data: CheckoutResponse) => {
    const directUrl = data?.url ?? data?.data?.url ?? data?.sessionUrl ?? data?.checkoutUrl;
    if (directUrl) { window.location.href = directUrl; return; }
    if (data?.id)  { window.location.href = `https://checkout.stripe.com/c/pay/${data.id}`; return; }
    throw new Error(`No checkout URL returned. Server: ${JSON.stringify(data)}`);
  };

  const handlePay = useCallback(async () => {
    if (!listing || !dropoff || !fees?.totalCharged) return;
    if (submittingRef.current) return; // hard lock — beats any state-update race
    submittingRef.current = true;

    setBookingErr("");
    setBooking(true);

    if (!idempotencyKeyRef.current) idempotencyKeyRef.current = makeIdempotencyKey();

    const durationMinutes = eta || Math.round((distKm / 40) * 60);

    try {
      sessionStorage.setItem(PENDING_RIDE_KEY, JSON.stringify({
        driverId: listing.driverId || driverId,
        driverName: listing.driverName || listing.fullName || "your driver",
        savedAt: Date.now(),
      }));
    } catch { /* sessionStorage may be unavailable (private mode) — non-fatal */ }

    try {
      const payload = {
        idempotencyKey: idempotencyKeyRef.current,

        riderId,
        listingId,
        driverId: listing.driverId || driverId,
        operatorId: listing.operatorId || listing.driverId || driverId,

        pickupLat: pickup.lat,
        pickupLng: pickup.lng,
        pickupAddress: pickup.name,
        dropoffLat: dropoff.lat,
        dropoffLng: dropoff.lng,
        dropoffAddress: dropoff.name,

        passengers: isPerP ? adults : 1,
        adults, children, infants, pets, serviceAnimals,
        seats: rt === "perSeat" ? seats : 1,

        rateType: rt,
        distanceKm: distKm,
        durationMinutes,
        durationDays: rt === "perDay" ? days : 0,
        baseFare: listing.baseFare || 0,
        perKmRate: listing.perKmRate || 0,
        perMinuteRate: listing.perMinuteRate || 0,
        surgeMultiplier: listing.surgeMultiplier || 1,

        selectedExtras,
        discountApplied: listing.discountApplied || {},

        rideSharing: sharing,
        notes,

        // Backend is expected to substitute the real tripId into successUrl
        // once it creates the trip record, e.g.
        // `${successUrl}&tripId=${tripId}&session_id={CHECKOUT_SESSION_ID}`.
        successUrl: `${window.location.origin}${window.location.pathname}?checkout=success`,
        cancelUrl: `${window.location.origin}${window.location.pathname}?checkout=cancelled`,
      };

      const res = await axios.post<CheckoutResponse>(RIDE_CHECKOUT_URL, payload, {
        headers: { "Idempotency-Key": idempotencyKeyRef.current },
      });
      redirectFromResponse(res.data);
      // Deliberately do NOT reset submittingRef/booking on the happy path —
      // the browser is about to navigate away to Stripe.
    } catch (e: any) {
      console.error("❌ Checkout error:", e?.response?.data || e?.message);
      const msg = e?.response?.data?.error?.message || e?.message || "Booking failed. Please try again.";
      setBookingErr(msg);
      setBooking(false);
      submittingRef.current = false;
      idempotencyKeyRef.current = ""; // allow a fresh key on manual retry
    }
  }, [listing, dropoff, fees, riderId, listingId, driverId, pickup, isPerP, adults, children,
      infants, pets, serviceAnimals, rt, seats, distKm, eta, days, selectedExtras, sharing, notes]);

  /* Cancel */
  const handleCancel = useCallback(async () => {
    if (!activeTrip?.tripId) return;
    try {
      await axios.post(CANCEL_RIDE_URL, { tripId: activeTrip.tripId, cancelledBy: riderId, reason: "rider_cancelled" });
      setActiveTrip(null); setTripId(null);
      navigate(-1);
    } catch (e: any) { setBookingErr(e?.response?.data?.error?.message || e.message || "Cancel failed"); }
  }, [activeTrip, riderId, navigate]);

  const dismissResumeNotice = () => {
    setResumeNotice(null);
    try { sessionStorage.removeItem(PENDING_RIDE_KEY); } catch { /* ignore */ }
  };
  const resumeCancelledBooking = () => {
    dismissResumeNotice();
    setStep(dropoff ? "pricing" : "options");
    setBookingErr("Your previous checkout was cancelled — review and try again.");
  };

  /* Derived */
  const tripStatus = activeTrip?.status || "requested";
  const sCfg        = STATUS_CFG[tripStatus] || STATUS_CFG.requested;
  const StatusIcon  = sCfg.Icon;
  const d2d         = pickup.lat && livePos ? hav(pickup.lat, pickup.lng, livePos.lat, livePos.lng) : null;
  const dEta        = d2d ? Math.max(1, Math.round((d2d / 40) * 60)) : iEta || null;
  const isDoneTrip  = ["completed", "cancelled", "no_driver"].includes(tripStatus);
  const canCancel   = ["requested", "accepted", "arriving"].includes(tripStatus);
  const stepLabel   = { dropoff: "Set Destination", options: "Ride Options", pricing: "Confirm & Pay", trip: "Your Ride" }[step];

  /* ── Panel content ── */
  const Panel = (
    <div className="rco-panel-inner">

      {step === "dropoff" && (
        <div className="rco-rev" style={{ "--d": "60ms" } as any}>
          <div className="rco-step-title">Where to?</div>
          <div className="rco-step-sub">Set your drop-off destination</div>

          {listing && (
            <div className="rco-dstrip">
              {listing.profilePhoto ? <img src={listing.profilePhoto} alt="" className="rco-avatar" /> : <div className="rco-avatar-fb"><Car size={22} /></div>}
              <div className="rco-d-info">
                <div className="rco-d-name">{listing.brand || ""} {listing.model || "Vehicle"}</div>
                <div className="rco-d-vehicle">{listing.driverName || "Your driver"} · {money(listing.rate || 0)} {rt === "perSurge" ? "base" : `/${RATE_LABEL[rt]}`}</div>
                {listing.rating && <div className="rco-d-rating"><Star size={10} fill="#D4900F" color="#D4900F" /> {listing.rating.toFixed(1)}</div>}
              </div>
              {listing.phone && (
                <a href={`tel:${listing.phone}`} className="rco-d-phone"><PhoneCall size={14} /></a>
              )}
            </div>
          )}

          <div className="rco-addr-block">
            <div className="rco-addr-row">
              <div className="rco-addr-dot rco-dot-g" />
              <div className="rco-addr-field"><div className="rco-addr-lbl">Pickup</div><div className="rco-addr-val">{pickup.name}</div></div>
            </div>
            <div className="rco-addr-row">
              <div className="rco-addr-dot rco-dot-r" />
              <div className="rco-addr-field">
                <div className="rco-addr-lbl">Destination</div>
                <input ref={inputRef} className="rco-addr-input" placeholder="Search destination…" autoComplete="off" />
              </div>
            </div>
          </div>
          {d2d !== null && <div className="rco-dist-row"><Car size={13} /> Driver is {d2d.toFixed(1)} km away · {dEta} min ETA</div>}
        </div>
      )}

      {step === "options" && listing && (
        <div className="rco-rev" style={{ "--d": "40ms" } as any}>
          <div className="rco-step-title">Ride Options</div>
          <div className="rco-step-sub">Customise your booking</div>
          <div className="rco-route-strip">
            <div className="rco-route-dot" style={{ background: "#1A9E6E" }} /><div className="rco-route-addr">{pickup.name}</div>
            <div className="rco-route-sep">→</div>
            <div className="rco-route-dot" style={{ background: "#C8202E" }} /><div className="rco-route-addr">{dropoff?.name}</div>
          </div>

          <div className="rco-rate-badge">
            {rt === "perSurge" ? <Sparkles size={11} /> : <Car size={11} />} {RATE_LABEL[rt]}
          </div>

          {isPerP && <>
            <CounterRow label="Adults" sub="Age 13+"             value={adults}         minVal={1} icon={<Users size={14} />}
              onInc={() => pax < maxCap && setAdults(a => a + 1)} onDec={() => setAdults(a => Math.max(1, a - 1))} />
            <CounterRow label="Children" sub="Age 2–12"          value={children}       icon={<Baby size={14} />}
              onInc={() => pax < maxCap && setChildren(c => c + 1)} onDec={() => setChildren(c => Math.max(0, c - 1))} />
            <CounterRow label="Infants" sub="Under 2 · Lap seat" value={infants}        icon={<Baby size={12} />}
              onInc={() => setInfants(i => i + 1)} onDec={() => setInfants(i => Math.max(0, i - 1))} />
            <CounterRow label="Pets" sub="Dogs, cats, etc."      value={pets}           icon={<Dog size={14} />}
              onInc={() => setPets(p => p + 1)} onDec={() => setPets(p => Math.max(0, p - 1))} />
            <CounterRow label="Service Animals" sub="Guide / assistance dogs" value={serviceAnimals} icon={<Dog size={14} />}
              onInc={() => setServiceAnimals(s => s + 1)} onDec={() => setServiceAnimals(s => Math.max(0, s - 1))} />
          </>}

          {rt === "perSeat" && <>
            <div className="rco-sect-lbl" style={{ marginTop: 4 }}>Seats Needed</div>
            <div className="rco-seat-grid">
              {Array.from({ length: maxCap }, (_, i) => i + 1).map(n => (
                <button key={n} className={`rco-seat-chip${seats === n ? " on" : ""}`} onClick={() => setSeats(n)}>
                  <Users size={11} />{n}
                </button>
              ))}
            </div>
          </>}

          {rt === "perDay" && (
            <CounterRow label="Days" sub="Full-day hire" value={days} minVal={1} icon={<Clock size={14} />}
              onInc={() => setDays(d => d + 1)} onDec={() => setDays(d => Math.max(1, d - 1))} />
          )}

          {(rt === "perCar" || rt === "perKm") && (
            <div className="rco-info-box">
              <Navigation size={14} color="#D4900F" style={{ flexShrink: 0, marginTop: 1 }} />
              <div className="rco-info-txt">
                {rt === "perKm"
                  ? `${money(listing.rate || 0)} per km — final price calculated at journey end based on actual distance.`
                  : `${money(listing.rate || 0)} flat fare for the full journey.`}
              </div>
            </div>
          )}

          {rt === "perSurge" && (
            <div className="rco-info-box">
              <Sparkles size={14} color="#D4900F" style={{ flexShrink: 0, marginTop: 1 }} />
              <div className="rco-info-txt">
                {money(listing.baseFare || 0)} base fare + {money(listing.perKmRate || 0)}/km + {money(listing.perMinuteRate || 0)}/min
                {(listing.surgeMultiplier || 1) > 1 ? ` · ${listing.surgeMultiplier}× surge right now` : ""}. Final price is confirmed at journey end.
              </div>
            </div>
          )}

          {extras.length > 0 && (
            <div className="rco-ext-sec">
              <div className="rco-ext-lbl">Optional Extras</div>
              {extras.map(([k, v]) => (
                <div key={k} className="rco-ext-row" onClick={() => toggleEx(k)}>
                  <div className={`rco-ext-chk${selectedExtras.includes(k) ? " on" : ""}`}>
                    {selectedExtras.includes(k) && <CheckCircle size={11} color="#fff" />}
                  </div>
                  <div className="rco-ext-name">{k}</div>
                  <div className="rco-ext-price">+{money(Number(v))}</div>
                </div>
              ))}
            </div>
          )}

          {listing.rideSharing && (
            <div className="rco-share-row" onClick={() => setSharing((s) => !s)}>
              <div><div className="rco-share-title">Ride Sharing</div><div className="rco-share-sub">Split cost with other passengers</div></div>
              <div className={`rco-tog${sharing ? " on" : ""}`}><div className="rco-tog-thumb" /></div>
            </div>
          )}

          <textarea className="rco-notes" placeholder="Notes for driver… (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
          <button className="rco-cont-btn" onClick={() => setStep("pricing")}>Review Price <ArrowRight size={14} /></button>
        </div>
      )}

      {step === "pricing" && listing && (
        <div className="rco-rev" style={{ "--d": "40ms" } as any}>
          <div className="rco-step-title">Confirm &amp; Pay</div>
          <div className="rco-step-sub">Review your fare before booking</div>
          <div className="rco-route-strip">
            <div className="rco-route-dot" style={{ background: "#1A9E6E" }} /><div className="rco-route-addr">{pickup.name}</div>
            <div className="rco-route-sep">→</div>
            <div className="rco-route-dot" style={{ background: "#C8202E" }} /><div className="rco-route-addr">{dropoff?.name}</div>
          </div>
          <div className="rco-price-card">
            <div className="rco-price-row">
              <span className="rco-price-lbl">
                {isPerP ? `${adults} adult${adults > 1 ? "s" : ""}` :
                  rt === "perSeat" ? `${seats} seat${seats > 1 ? "s" : ""}` :
                    rt === "perDay" ? `${days} day${days > 1 ? "s" : ""}` :
                      rt === "perKm" ? "Per km rate" :
                        rt === "perSurge" ? "Estimated fare" : "Base fare"}
              </span>
              <span className="rco-price-val">{money(fees?.baseRideCost)}</span>
            </div>
            {extrasTotal > 0 && (
              <div className="rco-price-row"><span className="rco-price-lbl">Extras</span><span className="rco-price-val">+{money(extrasTotal)}</span></div>
            )}
            <div className="rco-price-row"><span className="rco-price-lbl">Service fee</span><span className="rco-price-val" style={{ color: "var(--wm)" }}>{money(fees?.guestsFee)}</span></div>
            {eta > 0 && <div className="rco-price-row"><span className="rco-price-lbl">ETA</span><span className="rco-price-val" style={{ color: "var(--gn)" }}>{eta} min</span></div>}
            <div className="rco-price-div" />
            <div className="rco-total-row"><span className="rco-total-lbl">Total</span><span className="rco-total-val">{money(fees?.totalCharged)}</span></div>
            <div className="rco-price-row"><span className="rco-price-lbl" style={{ fontSize: 11 }}>Driver earns</span><span className="rco-price-val" style={{ color: "var(--gn)", fontSize: 11 }}>{money(fees?.driverProfit)}</span></div>
            {(rt === "perKm" || rt === "perSurge") && <div className="rco-price-note">* Final price based on actual GPS distance &amp; time — verified server-side</div>}
          </div>
          {bookingErr && <div className="rco-err"><AlertCircle size={16} style={{ flexShrink: 0 }} /><span>{bookingErr}</span><button onClick={() => setBookingErr("")}><X size={13} /></button></div>}
          <button className="rco-pay-btn" onClick={handlePay} disabled={booking || !fees?.totalCharged}>
            {booking ? <><Loader2 size={15} style={{ animation: "rcoSpin .8s linear infinite" }} /> Processing…</> : <><CreditCard size={15} /> Pay {money(fees?.totalCharged)}</>}
          </button>
          <button className="rco-back-lnk" onClick={() => setStep("options")}>← Edit options</button>
          <p className="rco-stripe-note">Redirected to Stripe Checkout · Secured &amp; encrypted</p>
        </div>
      )}

      {step === "trip" && (
        <div className="rco-rev" style={{ "--d": "40ms" } as any}>
          <div className="rco-step-title">Your Ride</div>
          <div className="rco-status-badge" style={{ borderColor: sCfg.color + "40", background: sCfg.color + "0D" }}>
            <StatusIcon size={22} color={sCfg.color} />
            <div>
              <div className="rco-status-lbl" style={{ color: sCfg.color }}>{sCfg.label}</div>
              <div className="rco-status-sub">{dEta && !isDoneTrip ? `${dEta} min ETA` : sCfg.sub}</div>
            </div>
          </div>
          {(activeTrip?.driverId || listing) && (
            <div className="rco-trip-driver">
              <div className="rco-trip-icon">
                {listing?.profilePhoto ? <img src={listing.profilePhoto} alt="" /> : <Car size={20} />}
              </div>
              <div className="rco-trip-info">
                <div className="rco-trip-info-lbl">Driver</div>
                <div className="rco-trip-info-val">{listing?.driverName || (activeTrip?.driverId || "").slice(0, 12) + "…" || "Your Driver"}</div>
              </div>
              {listing?.phone && <a href={`tel:${listing.phone}`} className="rco-d-phone"><PhoneCall size={14} /></a>}
            </div>
          )}
          <div className="rco-trip-route">
            <div className="rco-trip-addr"><div className="rco-trip-dot" style={{ background: "#1A9E6E" }} /><div className="rco-trip-txt">{pickup.name}</div></div>
            <div className="rco-trip-addr"><div className="rco-trip-dot" style={{ background: "#C8202E" }} /><div className="rco-trip-txt">{dropoff?.name || "Destination"}</div></div>
          </div>
          <div className="rco-trip-price-row">
            <span className="rco-trip-price-lbl">{tripStatus === "completed" ? "Total charged" : "Estimated total"}</span>
            <span className="rco-trip-price-val">{money(fees?.totalCharged)}</span>
          </div>
          {canCancel && <button className="rco-cancel-btn" onClick={handleCancel}><X size={13} /> Cancel Ride</button>}
          {tripStatus === "completed" && <button className="rco-done-btn" onClick={() => { setActiveTrip(null); setTripId(null); navigate("/"); }}><Home size={14} /> Done — Back to Home</button>}
          {tripStatus === "no_driver" && <button className="rco-done-btn" onClick={() => { setActiveTrip(null); setTripId(null); setStep("options"); }}><RotateCcw size={14} /> Try Again</button>}
        </div>
      )}
    </div>
  );

  return (
    <div className={`rco-root${mounted ? " rco-on" : ""}`}>
      <div className="rco-topbar" />

      {rtdbOffline && (
        <div className="rco-banner rco-banner-offline">
          <CloudOff size={13} /> Reconnecting to live driver data…
        </div>
      )}

      {resumeNotice && step !== "trip" && (
        <div className="rco-banner rco-banner-resume">
          <Info size={13} />
          Your last checkout with {resumeNotice.driverName} didn't finish.
          <button onClick={resumeCancelledBooking}>Resume</button>
          <button className="rco-banner-close" onClick={dismissResumeNotice}><X size={13} /></button>
        </div>
      )}

      <header className="rco-hdr">
        <div className="rco-logo" onClick={() => navigate("/")}>
          <div className="rco-logo-ring"><div className="rco-logo-ring-inner"><Sparkles size={12} color="#D4900F" /></div></div>
          <span className="rco-logo-txt">HEARTLAS</span>
        </div>
        <div className="rco-hdr-center">
          <button className="rco-back" onClick={() => navigate(-1)}><ChevronLeft size={17} /></button>
          <div className="rco-hdr-car">
            <div className="rco-hdr-model">{listing ? `${listing.brand || ""} ${listing.model || ""}`.trim() || "Confirm Ride" : stepLabel}</div>
            {listing && <div className="rco-hdr-sub">{money(listing.rate || 0)} · {RATE_LABEL[rt]}</div>}
          </div>
        </div>
        <div className="rco-hdr-r">
          {dEta && step !== "trip" && <div className="rco-eta-badge"><div className="rco-eta-num">{dEta}</div><div className="rco-eta-unit">min</div></div>}
        </div>
      </header>

      <div className="rco-body">
        <div className="rco-map-col">
          <div className="rco-map-wrap">
            <div id="rco-gmap" style={{ width: "100%", height: "100%" }} />
            {!isLoaded && <div className="rco-map-spinner"><div className="rco-spinner" /></div>}
          </div>
        </div>
        <div className="rco-panel-col">{Panel}</div>
        <div className="rco-mob-panel"><div className="rco-handle" />{Panel}</div>
      </div>
    </div>
  );
}